/**
 * Thema „News" (PV-037, A12): neue News aufgeklappt, darunter „Archiv (n)" zugeklappt; im Archiv
 * Titel + Datum, ein Klick auf den Titel zeigt den Text. Setzt „gelesen bis" NICHT selbst — das
 * macht die Hilfeseite beim Öffnen (das ⓘ-Popover darf es nicht).
 */
import type { NewsSprache } from '@projektverwaltung/shared';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { formatZeitpunkt } from '../../../lib/format-date.js';
import { NewsBesuchContext, newsInSprache, teileNeuArchiv, useNews } from '../../../lib/news.js';
import { MarkdownRenderer } from '../briefing-view.js';
import { HilfeHinweis } from './hilfe-hinweis.js';
import type { HilfeThemaProps } from './hilfe-typen.js';

export function ThemaNews({ kompakt }: HilfeThemaProps) {
  const { t, i18n } = useTranslation();
  const news = useNews();
  const besuch = useContext(NewsBesuchContext);
  if (news.isError) return <HilfeHinweis fehler>{t('hilfe.seite.fehler')}</HilfeHinweis>;
  // Hilfeseite: bis die frische Antwort da ist, nicht aus dem (evtl. alten) Cache aufteilen.
  if (!news.data || besuch?.status === 'wartet')
    return <HilfeHinweis>{t('hilfe.seite.laedt')}</HilfeHinweis>;

  const schrift = kompakt ? 'text-xs' : 'text-sm';
  if (news.data.items.length === 0) {
    return (
      <p className={schrift} style={{ color: 'var(--ink-3)' }}>
        {t('hilfe.news.keine')}
      </p>
    );
  }

  const sprache: NewsSprache = i18n.language.startsWith('en') ? 'en' : 'de';
  // Während des Besuchs gilt der beim Öffnen eingefrorene Stand — sonst rutschten die eben
  // gelesenen News ins Archiv, sobald der Server „gelesen" bestätigt (A12).
  const gelesenBis = besuch?.status === 'aktiv' ? besuch.gelesenBis : news.data.gelesen_bis;
  const { neu, archiv } = teileNeuArchiv(news.data.items, gelesenBis);
  const datum = (iso: string | null, ersatz: string) =>
    formatZeitpunkt(iso ?? ersatz, i18n.language, 'datum');

  return (
    <div className={`space-y-4 ${schrift}`} style={{ color: 'var(--ink-2)' }}>
      {neu.length === 0 ? (
        <p style={{ color: 'var(--ink-3)' }}>{t('hilfe.news.keine_neuen')}</p>
      ) : (
        neu.map((n) => {
          const inhalt = newsInSprache(n, sprache);
          return (
            <article
              key={n.news_id}
              className="space-y-2 rounded-lg border p-4"
              style={{ borderColor: 'var(--line-soft)', background: 'var(--surface)' }}
            >
              <header className="flex items-baseline justify-between gap-3">
                <h3 className="font-medium" style={{ color: 'var(--ink)' }}>
                  {inhalt.titel}
                </h3>
                <span className="shrink-0 text-xs" style={{ color: 'var(--ink-3)' }}>
                  {datum(n.veroeffentlicht_am, n.erstellt_am)}
                </span>
              </header>
              <MarkdownRenderer body={inhalt.text} />
            </article>
          );
        })
      )}
      {archiv.length > 0 && (
        <details className="rounded-lg border" style={{ borderColor: 'var(--line-soft)' }}>
          <summary className="cursor-pointer px-4 py-2 font-medium" style={{ color: 'var(--ink)' }}>
            {t('hilfe.news.archiv', { count: archiv.length })}
          </summary>
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {archiv.map((n) => {
              const inhalt = newsInSprache(n, sprache);
              return (
                <li key={n.news_id} className="px-4 py-2">
                  <details className="group">
                    <summary className="flex cursor-pointer items-baseline justify-between gap-3">
                      <span className="flex items-baseline gap-2">
                        <span
                          data-testid="aufklapp-zeichen"
                          aria-hidden="true"
                          className="inline-block text-xs transition-transform group-open:rotate-90"
                          style={{ color: 'var(--ink-3)' }}
                        >
                          ▸
                        </span>
                        {inhalt.titel}
                      </span>
                      <span className="shrink-0 text-xs" style={{ color: 'var(--ink-3)' }}>
                        {datum(n.veroeffentlicht_am, n.erstellt_am)}
                      </span>
                    </summary>
                    <div className="pt-2">
                      <MarkdownRenderer body={inhalt.text} />
                    </div>
                  </details>
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </div>
  );
}
